import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence } from "motion/react";
import { MARKETS, FALLBACK_MARKET, marketFromSlug, resolveLocale } from "@shared/markets";
import type { MarketConfig } from "@shared/markets";
import { I18nProvider } from "./i18n";
import { isLocale, type Locale } from "./i18n/config";
import { SiteProvider } from "./lib/site";
import { ToastProvider } from "./components/ui/Toast";
import ScrollTop from "./components/ui/ScrollTop";
import UpdatePrompt from "./components/ui/UpdatePrompt";
import { usePageViews } from "./lib/analytics";
import { AuthProvider, useAuth } from "./lib/auth";
import Shell from "./components/layout/Shell";
import PageSkeleton from "./components/ui/PageSkeleton";
import NavigationProgress, { RouteReady } from "./components/ui/NavigationProgress";

/**
 * 路由
 * ---------------------------------------------------------------
 * 網址結構：/:market/:locale/*   例如 /tw/zh-TW/jobs
 *
 * 為什麼市場要放進網址：
 *  - CDN 才能正確分開快取（同一個 /jobs 在不同市場內容完全不同）
 *  - hreflang 與 canonical 需要穩定可預測的網址
 *  - 使用者把連結貼給別人時，對方看到的是同一個市場的內容
 *
 * 沒有前綴的網址由 Worker 依 IP 轉址，前端這裡只做保底。
 */

// 首頁以外的頁面都切成獨立 chunk，首屏只載入需要的
const Home = lazy(() => import("./routes/Home"));
const Jobs = lazy(() => import("./routes/Jobs"));
const JobDetail = lazy(() => import("./routes/JobDetail"));
const Talent = lazy(() => import("./routes/Talent"));
const TalentDetail = lazy(() => import("./routes/TalentDetail"));
const PostJob = lazy(() => import("./routes/PostJob"));
const Categories = lazy(() => import("./routes/Categories"));
const HowItWorks = lazy(() => import("./routes/HowItWorks"));
const Pricing = lazy(() => import("./routes/Pricing"));
const Rates = lazy(() => import("./routes/Rates"));
const Membership = lazy(() => import("./routes/Membership"));
const Terms = lazy(() => import("./routes/Terms"));
const Privacy = lazy(() => import("./routes/Privacy"));
const Cookies = lazy(() => import("./routes/Cookies"));
const DashMessages = lazy(() => import("./routes/dashboard/Messages"));
const DashContracts = lazy(() => import("./routes/dashboard/Contracts"));
const DashProposals = lazy(() => import("./routes/dashboard/Proposals"));
const DashMyJobs = lazy(() => import("./routes/dashboard/MyJobs"));
const DashSettings = lazy(() => import("./routes/dashboard/Settings"));
const DashOffice = lazy(() => import("./routes/dashboard/Office"));
const DashFinance = lazy(() => import("./routes/dashboard/Finance"));
const DashClients = lazy(() => import("./routes/dashboard/Clients"));
const DashTime = lazy(() => import("./routes/dashboard/Time"));
const DashExpenses = lazy(() => import("./routes/dashboard/Expenses"));
const DashAwayAgent = lazy(() => import("./routes/dashboard/AwayAgent"));
const DashReferral = lazy(() => import("./routes/dashboard/Referral"));
const About = lazy(() => import("./routes/About"));
const Careers = lazy(() => import("./routes/Careers"));
const Press = lazy(() => import("./routes/Press"));
const InvoiceView = lazy(() => import("./routes/InvoiceView"));
const ProjectProgress = lazy(() => import("./routes/ProjectProgress"));
const VerifyEmail = lazy(() => import("./routes/VerifyEmail"));
const VerifyPhone = lazy(() => import("./routes/VerifyPhone"));
const EmailChange = lazy(() => import("./routes/EmailChange"));
const VerifyId = lazy(() => import("./routes/VerifyId"));
const Dashboard = lazy(() => import("./routes/Dashboard"));
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));
const RequireAuth = lazy(() => import("./components/RequireAuth"));
const Login = lazy(() => import("./routes/Login"));
const Signup = lazy(() => import("./routes/Signup"));
const ForgotPassword = lazy(() => import("./routes/ForgotPassword"));
const ResetPassword = lazy(() => import("./routes/ResetPassword"));
const NotFound = lazy(() => import("./routes/NotFound"));

export default function App() {
  return (
    <Routes>
      <Route path="/:market/:locale/*" element={<LocalisedApp />} />
      {/* 沒帶前綴時的保底轉址；正常情況 Worker 已經處理掉了 */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function RootRedirect() {
  const injected = window.__COLOURDEV__;
  const market = MARKETS[injected?.market ?? FALLBACK_MARKET] ?? MARKETS[FALLBACK_MARKET];
  const locale = injected?.locale ?? market.defaultLocale;
  return <Navigate to={`/${market.slug}/${locale}${window.location.pathname}`} replace />;
}

/**
 * 網址上的市場必須和帳號綁定的市場一致
 * ═══════════════════════════════════════════════════════════════
 *
 * gateway 的規則是：登入之後一律用帳號綁定的市場，網址、query string
 * 與 IP 全部不算數。那條規則是對的 —— 否則改一下網址就能跨市場交易，
 * 而「只跟同一個司法管轄區的人成交」是這個平台的核心承諾。
 *
 * 但前端沒有跟著那條規則走，於是出現這個畫面：
 *
 *   綁在 TW 的會員打開 /us/en/talent
 *   → 網址說美國、頁首徽章說 United States
 *   → API 依帳號回傳台灣的接案者
 *
 * 三者互相矛盾，而使用者看到的結論是「市場隔離沒有在運作」。
 * 用 VPN 也改變不了，因為登入之後根本不看 IP。
 *
 * 修法不是放寬後端，是讓網址跟上：登入者一律轉回自己的市場，
 * 路徑與 query 都保留，只換市場前綴。
 */
function MarketGuard({ market, locale }: { market: MarketConfig; locale: string }) {
  const { actor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!actor) return;
    const own = MARKETS[actor.market.toUpperCase()];
    if (!own || own.code === market.code) return;

    // 只換市場前綴，其餘原封不動 —— 使用者停在同一頁，只是換到自己的市場
    const rest = location.pathname.slice(`/${market.slug}/${locale}`.length);
    navigate(`/${own.slug}/${resolveLocale(own, locale)}${rest}${location.search}`, {
      replace: true,
    });
  }, [actor, market.code, market.slug, locale, location.pathname, location.search, navigate]);

  return null;
}

function LocalisedApp() {
  const { market: marketSlug, locale: localeParam } = useParams();
  const location = useLocation();

  const market = marketFromSlug(marketSlug);
  if (!market) return <RootRedirect />;

  const resolved = resolveLocale(market, localeParam);
  if (resolved !== localeParam || !isLocale(resolved)) {
    return <Navigate to={`/${market.slug}/${resolved}`} replace />;
  }

  return (
    <SiteProvider market={market}>
      <AuthProvider>
        <MarketGuard market={market} locale={resolved} />
        <I18nProvider
          locale={resolved as Locale}
          marketSlug={market.slug}
          marketCurrency={market.currency}
        >
          <ToastProvider>
        {/*
          換頁時最上緣的進度條。

          它在 AnimatePresence 與 Suspense **外面** —— 路由卸載、
          骨架屏頂替、新頁掛載，這三件事發生的時候它都不會被拆掉。
          從按下到畫出來，畫面上一直有同一個東西在動。
        */}
        <NavigationProgress />

        {/* AnimatePresence + key=pathname → 每次換頁都有轉場動畫 */}
        <AnimatePresence mode="wait" initial={false}>
          <Suspense fallback={<PageSkeleton />}>
            {/*
              在 Suspense 裡面宣告「這一頁畫出來了」。

              放在外面的話，effect 會在 chunk 還在下載時就跑掉 ——
              進度條會先跑完，然後畫面才開始等。那比沒有進度條更糟。
            */}
            <RouteReady />
            <Routes location={location} key={location.pathname}>
              {/* 對外頁面：頁首 + 頁尾 */}
              <Route element={<Shell />}>
                <Route index element={<Home />} />
                <Route path="jobs" element={<Jobs />} />
                <Route path="jobs/:slug" element={<JobDetail />} />
                <Route path="talent" element={<Talent />} />
                <Route path="talent/:handle" element={<TalentDetail />} />
                <Route path="post-job" element={<PostJob />} />
                <Route path="categories" element={<Categories />} />
                <Route path="categories/:slug" element={<Jobs />} />
                <Route path="how-it-works" element={<HowItWorks />} />
                <Route path="about" element={<About />} />
                <Route path="careers" element={<Careers />} />
                <Route path="press" element={<Press />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="rates" element={<Rates />} />
                {/* 全站唯一的結帳流程。?order=... 時顯示付款結果。 */}
                <Route path="membership" element={<Membership />} />
                <Route path="membership/return" element={<Membership />} />
                <Route path="membership/cancel" element={<Membership />} />
                <Route path="terms" element={<Terms />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="cookies" element={<Cookies />} />

                {/*
                  請款單的公開檢視。刻意放在需要登入的區塊之外 ——
                  客戶多半不是平台使用者，逼他註冊才能看一張寄給他的
                  帳單是最糟的體驗。授權靠網址裡那串不可猜的 token。
                */}
                <Route path="invoice/:token" element={<InvoiceView />} />
                {/* 客戶看專案進度。同樣不需要登入 —— 授權靠網址裡的 token。 */}
                <Route path="project/:token" element={<ProjectProgress />} />
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="verify-email" element={<VerifyEmail />} />
                {/* 投標與主動聯絡被擋下時，兩個 gate 都指向這裡 */}
                <Route path="verify-phone" element={<VerifyPhone />} />
                {/* 換信箱的兩段確認。不需要登入 —— 授權靠網址裡的 token */}
                <Route path="email-change" element={<EmailChange />} />
                {/* 證件驗證。人才市場的「已驗證身分」徽章來自這裡 */}
                <Route path="verify-id" element={<VerifyId />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/*
                行動辦公室：需要登入，而且有自己的側欄版面。
                RequireAuth 放在外層 —— 未登入時連版面都不該渲染，
                否則會先閃一下側欄才跳走。
              */}
              <Route element={<RequireAuth />}>
                <Route path="dashboard" element={<DashboardLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="messages" element={<DashMessages />} />
                  <Route path="referral" element={<DashReferral />} />
                  <Route path="contracts" element={<DashContracts />} />
                  <Route path="proposals" element={<DashProposals />} />
                  <Route path="jobs" element={<DashMyJobs />} />
                  <Route path="settings" element={<DashSettings />} />
                  <Route path="office" element={<DashOffice />} />
                  <Route path="finance" element={<DashFinance />} />
                  <Route path="clients" element={<DashClients />} />
                  <Route path="time" element={<DashTime />} />
                  <Route path="expenses" element={<DashExpenses />} />
                  <Route path="agent" element={<DashAwayAgent />} />
                  {/* 其餘子路徑仍導向總覽，避免出現空白頁 */}
                  <Route path=":section" element={<Dashboard />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </AnimatePresence>
            <ScrollTop />
            {/* 部署新版時提醒重新整理 —— SPA 不會自己換掉已載入的程式 */}
            <UpdatePrompt />
            {/* 路由變動時送 page_view —— 單頁應用不會自己送 */}
            <PageViews />
          </ToastProvider>
        </I18nProvider>
      </AuthProvider>
    </SiteProvider>
  );
}

declare global {
  interface Window {
    __COLOURDEV__?: {
      market: string;
      marketSlug: string;
      locale: string;
      currency: string;
      paymentProvider: string;
    };
  }
}


/**
 * 只為了呼叫 usePageViews 而存在的空元件。
 *
 * 掛在這裡而不是 Shell：Shell 只包對外頁面，行動辦公室走的是
 * DashboardLayout —— 放在 Shell 會漏掉登入後的所有頁面，
 * 而那正是最想知道有沒有人在用的部分。
 */
function PageViews() {
  usePageViews();
  return null;
}
